import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Divider, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { getMm } from "../../theme/modernMaterialEnterprise";

const NUNITO = "'Plus Jakarta Sans', sans-serif";

function formatPrice(cents, currency = "SEK") {
  const n = Number(cents);
  if (Number.isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("sv-SE", { style: "currency", currency }).format(n / 100);
  } catch {
    return `${(n / 100).toFixed(2)} ${currency}`;
  }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("sv-SE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusChipSx(status, isDarkMode) {
  switch (status) {
    case "paid":
    case "delivered":
      return { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" };
    case "cancelled":
    case "expired":
      return { bg: getMm(isDarkMode).surfaceContainer, color: getMm(isDarkMode).onSurfaceVariant, border: getMm(isDarkMode).outlineVariant };
    default:
      return { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" };
  }
}

export default function PosShopReceipt({ order, receiptFooter = "", isDarkMode = false, showPrint = true }) {
  const { t } = useTranslation();
  const printRef = useRef(null);

  if (!order) return null;

  const sc = statusChipSx(order.status, isDarkMode);
  const border = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ width: "100%" }}>
      {showPrint && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5, "@media print": { display: "none" } }}>
          <Button
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: "none", fontFamily: NUNITO, color: muted }}
          >
            {t("pos_receipt_print")}
          </Button>
        </Box>
      )}

      <Box
        ref={printRef}
        id="pos-shop-receipt"
        sx={{
          textAlign: "left",
          p: 2.5,
          borderRadius: "8px",
          border,
          bgcolor: getMm(isDarkMode).surfaceLowest,
          fontFamily: NUNITO,
          "@media print": {
            border: "1px solid #ccc",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: text, mb: 0.5 }}>
              {t("pos_receipt_title")}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: muted }}>
              {t("pos_receipt_order_id")}: {String(order.id).slice(0, 8).toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={t(`pos_order_status_${order.status}`, order.status)}
            sx={{
              bgcolor: sc.bg,
              color: sc.color,
              border: `1px solid ${sc.border}`,
              fontWeight: 600,
              fontFamily: NUNITO,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: "0.875rem", color: muted, mb: 2 }}>
          {formatDateTime(order.paidAt || order.createdAt)}
        </Typography>

        {(order.buyerName || order.buyerEmail) && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: muted, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
              {t("pos_receipt_buyer")}
            </Typography>
            {order.buyerName && (
              <Typography sx={{ fontSize: "0.9375rem", color: text, fontWeight: 600 }}>{order.buyerName}</Typography>
            )}
            {order.buyerEmail && (
              <Typography sx={{ fontSize: "0.875rem", color: muted }}>{order.buyerEmail}</Typography>
            )}
          </Box>
        )}

        <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, mb: 1.5 }} />

        <Box sx={{ mb: 1.5 }}>
          {(order.items || []).map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 0.75,
                borderBottom: isDarkMode ? "1px solid #1F2937" : "1px solid #F3F4F6",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.9375rem", color: text, fontWeight: 500 }}>
                  {item.quantity}× {item.productName}
                </Typography>
                {item.productSku && (
                  <Typography sx={{ fontSize: "0.75rem", color: muted }}>{item.productSku}</Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.9375rem", color: text, whiteSpace: "nowrap" }}>
                {formatPrice(item.lineTotalCents, order.currency)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1 }}>
          <Typography sx={{ fontWeight: 700, color: text }}>{t("pos_receipt_total")}</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: text }}>
            {formatPrice(order.subtotalCents, order.currency)}
          </Typography>
        </Box>

        {order.status === "delivered" && order.deliveredAt && (
          <Typography sx={{ fontSize: "0.8125rem", color: muted, mt: 1.5 }}>
            {t("pos_receipt_delivered_at", { date: formatDateTime(order.deliveredAt) })}
          </Typography>
        )}

        {receiptFooter?.trim() && (
          <>
            <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, my: 2 }} />
            <Typography sx={{ fontSize: "0.8125rem", color: muted, whiteSpace: "pre-wrap" }}>
              {receiptFooter.trim()}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
